import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { adminClassificationApi } from '@/lib/api';
import type { ClassificationSettings } from '@/types';
import { toast } from 'sonner';

export function ClassificationSettingsPanel() {
  const [settings, setSettings] = React.useState<ClassificationSettings | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    adminClassificationApi
      .getSettings()
      .then(setSettings)
      .catch(() => toast.error('Failed to load classification settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;

    if (settings.minHistoryCount < 1) {
      toast.error('Minimum history count must be at least 1');
      return;
    }
    if (settings.minHistoryShare <= 0 || settings.minHistoryShare > 1) {
      toast.error('Minimum history share must be between 0 (exclusive) and 1 (inclusive)');
      return;
    }

    setSaving(true);
    try {
      const updated = await adminClassificationApi.updateSettings(settings);
      setSettings(updated);
      toast.success('Classification settings saved');
    } catch {
      toast.error('Failed to save classification settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Auto-Classification Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Auto-Classification Settings</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Global thresholds for merchant-based auto-classification. These apply to all budgets with auto-classify enabled.
          </p>

          <Input
            label="Minimum history count"
            type="number"
            min={1}
            step={1}
            value={settings.minHistoryCount}
            onChange={(e) =>
              setSettings({ ...settings, minHistoryCount: Number(e.target.value) })
            }
          />

          <Input
            label="Minimum history share"
            type="number"
            min={0.01}
            max={1}
            step={0.01}
            value={settings.minHistoryShare}
            onChange={(e) =>
              setSettings({ ...settings, minHistoryShare: Number(e.target.value) })
            }
          />

          <Button type="submit" loading={saving}>
            Save
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
